import { gql, useEnhancedMutation } from "@uplift-ltd/apollo";
import { SpaceBetween } from "@uplift-ltd/space-between";
import Router from "next/router";
import React from "react";
import * as yup from "yup";
import {
  EnhancedFormik,
  Form,
  FormStatus,
  InputGroupField,
  SubmitButton,
} from "components/common/Form";
import Page from "components/common/Page";
import { EMAIL_PLACEHOLDER } from "constants/site";
import { PROFILE_URL } from "constants/urls";
import { assertIsError } from "helpers/errors";
import { getUsernameFromEmail } from "helpers/strings";
import { useRouterQuery } from "hooks/useRouterQuery";
import { LoginUserMutation, LoginUserVariables } from "./__generated__/Login";
import {
  ResetPasswordMutation,
  ResetPasswordVariables,
  SendPasswordResetEmailMutation,
  SendPasswordResetEmailVariables,
} from "./__generated__/ResetPassword";
import Button from "./common/Button";
import Container from "./common/Container";

const statusMessages = {
  invalidEmail: "Email address did not match. Please try again.",
  genericError: "Oops. Something went wrong. Please try again later.",
  successfulEmail: "An email has been sent with a link to reset your password.",
  successfulPasswordChange: "Your password has been reset. Logging you in, please wait…",
};

const LOGIN_USER = gql`
  mutation LoginUserAfterPasswordReset($username: String!, $password: String!) {
    loginWithCookies(
      input: {
        clientMutationId: "LoginUserAfterPasswordReset"
        login: $username
        password: $password
      }
    ) {
      status
    }
  }
`;

const RESET_PASSWORD = gql`
  mutation ResetPassword($key: String!, $login: String!, $password: String!) {
    resetUserPassword(
      input: { clientMutationId: "ResetPassword", key: $key, login: $login, password: $password }
    ) {
      clientMutationId
    }
  }
`;

const RESET_PASSWORD_FORM_INITIAL_VALUES = {
  password: "",
  passwordConfirm: "",
};

interface ResetPasswordFormValues {
  password: string;
  passwordConfirm: string;
}

const resetPasswordLabels: Record<keyof ResetPasswordFormValues, string> = {
  password: "Password",
  passwordConfirm: "Password (again)",
};

const resetPasswordValidationSchema = yup.object().shape({
  password: yup.string().required().min(8).max(64).label(resetPasswordLabels.password),

  passwordConfirm: yup
    .string()
    .required()
    .oneOf([yup.ref("password")], "Password must match.")
    .label(resetPasswordLabels.passwordConfirm),
});

interface ResetPasswordFormProps {
  resetKey: string;
  email: string;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ resetKey, email }) => {
  const [resetPassword] = useEnhancedMutation<ResetPasswordMutation, ResetPasswordVariables>(
    RESET_PASSWORD,
    {},
    { auth: false }
  );

  const [loginUser] = useEnhancedMutation<LoginUserMutation, LoginUserVariables>(
    LOGIN_USER,
    {},
    { auth: false }
  );

  return (
    <EnhancedFormik<ResetPasswordFormValues>
      initialValues={RESET_PASSWORD_FORM_INITIAL_VALUES}
      validationSchema={resetPasswordValidationSchema}
      resetStatusOnSubmit
      onSubmit={async (values, { resetForm, setFormSuccess, setFormError }) => {
        try {
          const passwordResetResult = await resetPassword({
            variables: {
              password: values.password,
              login: getUsernameFromEmail(email),
              key: resetKey,
            },
          });

          if (passwordResetResult.data?.resetUserPassword?.clientMutationId) {
            resetForm();
            setFormSuccess(statusMessages.successfulPasswordChange);

            const loginResult = await loginUser({
              variables: {
                username: email,
                password: values.password,
              },
            });

            if (loginResult.data?.loginWithCookies) {
              const { status } = loginResult.data.loginWithCookies;
              if (status === "SUCCESS") {
                window.location.assign(PROFILE_URL);
              } else {
                throw new Error(statusMessages.genericError);
              }
            } else {
              throw new Error(statusMessages.genericError);
            }
          } else {
            throw new Error(statusMessages.genericError);
          }
        } catch (err) {
          assertIsError(err);
          setFormError(err);
        }
      }}
    >
      <Form>
        <SpaceBetween className="mt-4">
          <FormStatus />
          <InputGroupField
            name="password"
            label={resetPasswordLabels.password}
            type="password"
            helpText="Passwords must be strong, 8 to 64 characters long."
            required
          />
          <InputGroupField
            name="passwordConfirm"
            label={resetPasswordLabels.passwordConfirm}
            type="password"
            required
          />
          <div className="text-center">
            <SubmitButton className="mb-4">Update Password and Log In</SubmitButton>
          </div>
        </SpaceBetween>
      </Form>
    </EnhancedFormik>
  );
};

const SEND_PASSWORD_RESET_EMAIL = gql`
  mutation SendPasswordResetEmail($username: String!) {
    sendPasswordResetEmail(
      input: { clientMutationId: "SendPasswordResetEmail", username: $username }
    ) {
      clientMutationId
    }
  }
`;

const SEND_PASSWORD_RESET_EMAIL_FORM_INITIAL_VALUES = {
  email: "",
};

interface SendPasswordResetEmailFormValues {
  email: string;
}

const sendPasswordResetEmailFormLabels: Record<keyof SendPasswordResetEmailFormValues, string> = {
  email: "Email Address",
};

const sendPasswordResetEmailValidationSchema = yup.object().shape({
  email: yup.string().email().required().label(sendPasswordResetEmailFormLabels.email),
});

interface SendPasswordResetEmailFormProps {
  email?: string;
}

const SendPasswordResetEmailForm = ({ email = "" }: SendPasswordResetEmailFormProps) => {
  const [sendPasswordResetEmail] = useEnhancedMutation<
    SendPasswordResetEmailMutation,
    SendPasswordResetEmailVariables
  >(SEND_PASSWORD_RESET_EMAIL, {}, { auth: false });

  return (
    <EnhancedFormik<SendPasswordResetEmailFormValues>
      initialValues={{ ...SEND_PASSWORD_RESET_EMAIL_FORM_INITIAL_VALUES, email }}
      validationSchema={sendPasswordResetEmailValidationSchema}
      onSubmit={async (values, { resetForm, setFormSuccess, setFormError }) => {
        setFormError(null);

        const result = await sendPasswordResetEmail({
          variables: {
            username: values.email,
          },
        });

        if (result.data?.sendPasswordResetEmail?.clientMutationId) {
          resetForm();
          setFormSuccess(statusMessages.successfulEmail);
        } else {
          setFormError(statusMessages.genericError);
        }
      }}
    >
      <Form>
        <SpaceBetween className="mt-4">
          <FormStatus
            errorCodeMap={{
              invalid_email: statusMessages.invalidEmail,
            }}
          />
          <InputGroupField
            name="email"
            label={sendPasswordResetEmailFormLabels.email}
            placeholder={EMAIL_PLACEHOLDER}
            required
          />
          <div className="text-center">
            <SubmitButton>Send password reset email</SubmitButton>
          </div>
        </SpaceBetween>
      </Form>
    </EnhancedFormik>
  );
};

const ResetPasswordPage: React.FC = () => {
  const {
    routerQuery: { key, email },
  } = useRouterQuery<{ email?: string; key: string }>();

  return (
    <Container>
      <Page title="Password Reset">
        {key && email ? (
          <ResetPasswordForm resetKey={key} email={email} />
        ) : (
          <SendPasswordResetEmailForm email={email} />
        )}
        <div className="text-center">
          <Button theme="textButton" onClick={() => Router.back()}>
            Go Back
          </Button>
        </div>
      </Page>
    </Container>
  );
};

export default ResetPasswordPage;
